import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { apiErr } from "../lib/utils";
import { Input } from "./ui/input";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "./ui/select";
import { Leaf, Users, ShieldCheck, Plus, Trash2, Check, X } from "lucide-react";
import { toast } from "sonner";

const PILLAR_META = {
  Environment: { icon: Leaf, color: "text-[#00E65B]" },
  Social: { icon: Users, color: "text-blue-400" },
  Governance: { icon: ShieldCheck, color: "text-[#FFB800]" },
};

export function EsgTaxonomyManager({ canEdit }) {
  const [taxonomy, setTaxonomy] = useState({});
  const [newCat, setNewCat] = useState({ pillar: "Environment", category: "" });
  const [activityFor, setActivityFor] = useState(null); // category id
  const [newAct, setNewAct] = useState("");

  const load = () => api.get("/esg-taxonomy").then((r) => setTaxonomy(r.data.taxonomy)).catch((e) => toast.error(apiErr(e)));
  useEffect(() => { load(); }, []);

  const addCategory = async () => {
    if (!newCat.category.trim()) return;
    try { await api.post("/esg-taxonomy/category", { pillar: newCat.pillar, category: newCat.category.trim() }); setNewCat({ ...newCat, category: "" }); load(); toast.success("Category added"); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const delCategory = async (id) => {
    if (!window.confirm("Delete this category and its activity types?")) return;
    try { await api.delete(`/esg-taxonomy/category/${id}`); load(); } catch (e) { toast.error(apiErr(e)); }
  };
  const addActivity = async (id) => {
    if (!newAct.trim()) return;
    try { await api.post("/esg-taxonomy/activity", { category_id: id, activity_type: newAct.trim() }); setNewAct(""); setActivityFor(null); load(); toast.success("Activity type added"); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const delActivity = async (id, act) => {
    try { await api.delete("/esg-taxonomy/activity", { params: { category_id: id, activity_type: act } }); load(); } catch (e) { toast.error(apiErr(e)); }
  };

  return (
    <div className="space-y-6" data-testid="esg-taxonomy-manager">
      <p className="text-sm text-muted-foreground">Master table of <span className="text-foreground">Pillar → Category → Activity Type</span> used by the unified Add-ESG-Data form. {canEdit ? "Add or remove items below; changes apply to new data entries." : "Read-only — Admin or ESG Manager can edit."}</p>

      {canEdit && (
        <div className="card-flat p-3 flex flex-wrap items-end gap-2" data-testid="taxonomy-add-category">
          <div><div className="label-eyebrow text-[10px] mb-1">Pillar</div>
            <Select value={newCat.pillar} onValueChange={(v) => setNewCat({ ...newCat, pillar: v })}>
              <SelectTrigger className="w-44" data-testid="new-cat-pillar"><SelectValue /></SelectTrigger>
              <SelectContent>{Object.keys(PILLAR_META).map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="flex-1 min-w-[180px]"><div className="label-eyebrow text-[10px] mb-1">New category</div>
            <Input value={newCat.category} onChange={(e) => setNewCat({ ...newCat, category: e.target.value })} placeholder="e.g. Supply Chain" onKeyDown={(e) => e.key === "Enter" && addCategory()} data-testid="new-cat-input" /></div>
          <Button variant="primary" onClick={addCategory} data-testid="new-cat-add"><Plus className="h-4 w-4" /> Add category</Button>
        </div>
      )}

      {Object.keys(PILLAR_META).map((pillar) => {
        const Icon = PILLAR_META[pillar].icon;
        const cats = taxonomy[pillar] || [];
        return (
          <div key={pillar} data-testid={`taxonomy-pillar-${pillar.toLowerCase()}`}>
            <div className="flex items-center gap-2 mb-2"><Icon className={`h-4 w-4 ${PILLAR_META[pillar].color}`} /><h3 className="font-display font-semibold">{pillar}</h3><Badge variant="outline" className="text-[10px]">{cats.length} categories</Badge></div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {cats.map((c) => (
                <div key={c.id} className="card-flat p-3" data-testid={`taxonomy-cat-${c.id}`}>
                  <div className="flex items-center justify-between mb-2">
                    <div className="font-medium text-sm">{c.category}{c.system && <Badge variant="outline" className="ml-2 text-[8px]">default</Badge>}</div>
                    {canEdit && <Button size="icon" variant="ghost" className="h-6 w-6" onClick={() => delCategory(c.id)} data-testid={`del-cat-${c.id}`}><Trash2 className="h-3 w-3 text-red-400" /></Button>}
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    {(c.activity_types || []).map((a) => (
                      <span key={a} className="inline-flex items-center gap-1 text-[11px] bg-surface-hover border border-border rounded-sm px-2 py-0.5">
                        {a}{canEdit && <button onClick={() => delActivity(c.id, a)} className="text-muted-foreground hover:text-red-400" data-testid={`del-act-${c.id}-${a}`}><X className="h-2.5 w-2.5" /></button>}
                      </span>
                    ))}
                    {(c.activity_types || []).length === 0 && <span className="text-[11px] text-muted-foreground">No activity types yet</span>}
                  </div>
                  {canEdit && (activityFor === c.id ? (
                    <div className="flex items-center gap-1.5 mt-2">
                      <Input autoFocus value={newAct} onChange={(e) => setNewAct(e.target.value)} placeholder="New activity type" className="h-7 text-xs" onKeyDown={(e) => e.key === "Enter" && addActivity(c.id)} data-testid={`act-input-${c.id}`} />
                      <Button size="icon" variant="primary" className="h-7 w-7" onClick={() => addActivity(c.id)} data-testid={`act-confirm-${c.id}`}><Check className="h-3 w-3" /></Button>
                      <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => { setActivityFor(null); setNewAct(""); }}><X className="h-3 w-3" /></Button>
                    </div>
                  ) : (
                    <button className="text-[11px] text-accent inline-flex items-center gap-0.5 mt-2" onClick={() => { setActivityFor(c.id); setNewAct(""); }} data-testid={`add-act-${c.id}`}><Plus className="h-3 w-3" /> Add activity type</button>
                  ))}
                </div>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}
